package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.datetime.*
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrepScreen(
    onBack: () -> Unit,
    onNavigateToManagement: () -> Unit,
    onNavigateToRoutines: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    val userRole = LocalUserRole.current
    val canEdit = userRole == UserRole.ADMIN || userRole == UserRole.EDITOR

    val today = getCurrentLocalDate()
    val todayStr = today.toString()

    val dates = remember {
        (-3..3).map { i ->
            today.plus(i, DateTimeUnit.DAY)
        }
    }

    var selectedDate by remember { mutableStateOf(today) }
    var plannedItems by remember { mutableStateOf<List<PlannedPrepItem>>(emptyList()) }
    var masterItems by remember { mutableStateOf<List<PrepItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    var showAddModal by remember { mutableStateOf(false) }
    var itemToDelete by remember { mutableStateOf<PlannedPrepItem?>(null) }
    var viewingRecipeItem by remember { mutableStateOf<PlannedPrepItem?>(null) }

    fun loadPrep(dateStr: String) {
        scope.launch {
            isLoading = true
            repository.getPlannedPrepItems(dateStr).onSuccess { items ->
                plannedItems = items
            }.onFailure {
                plannedItems = emptyList()
            }
            repository.getMasterPrepItems().onSuccess { masters ->
                masterItems = masters
            }
            isLoading = false
        }
    }

    LaunchedEffect(selectedDate) {
        loadPrep(selectedDate.toString())
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Prep List") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    if (canEdit) {
                        IconButton(onClick = onNavigateToManagement) {
                            Image(
                                painter = painterResource(Res.drawable.maintenance),
                                contentDescription = "Manage Master Prep",
                                modifier = Modifier.size(24.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                        IconButton(onClick = onNavigateToRoutines) {
                            Image(
                                painter = painterResource(Res.drawable.calendar),
                                contentDescription = "Edit Templates",
                                modifier = Modifier.size(24.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            val collapse = rememberScrollCollapse()
            val listState = rememberLazyListState()
            val nested = remember(listState, collapse) {
                collapse.connection {
                    listState.firstVisibleItemIndex == 0 && listState.firstVisibleItemScrollOffset == 0
                }
            }
            Column(modifier = Modifier.fillMaxSize()) {
                CollapsingDateTabs(
                    dates = dates,
                    selectedDate = selectedDate,
                    today = today,
                    collapse = collapse.fraction,
                    onDateSelected = { selectedDate = it },
                    onExpand = { collapse.expand() }
                )

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {

                if (canEdit) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = { showAddModal = true },
                            modifier = Modifier.weight(1f).height(46.dp),
                            shape = MaterialTheme.shapes.medium
                        ) {
                            Text("+ Add Prep Item", fontWeight = FontWeight.Bold)
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        OutlinedButton(
                            onClick = onNavigateToManagement,
                            modifier = Modifier.fillMaxWidth().height(40.dp),
                            shape = MaterialTheme.shapes.medium
                        ) {
                            Text("Manage Master Items")
                        }
                    }
                }

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (plannedItems.isEmpty()) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        Text("No prep items for this date.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.outline)
                    }
                } else {
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxWidth().weight(1f).nestedScroll(nested),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(items = plannedItems, key = { it.id }) { item ->
                            val isDone = item.isTicked == 1
                            Card(
                                modifier = Modifier.fillMaxWidth(),
                                shape = MaterialTheme.shapes.medium,
                                colors = CardDefaults.cardColors(
                                    containerColor = if (isDone) successContainerColor() else MaterialTheme.colorScheme.surface
                                ),
                                border = BorderStroke(1.dp, if (isDone) successContentColor() else MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Checkbox(
                                            checked = isDone,
                                            onCheckedChange = { checked ->
                                                plannedItems = plannedItems.map { 
                                                    if (it.id == item.id) it.copy(isTicked = if (checked) 1 else 0) else it 
                                                }
                                                scope.launch {
                                                    repository.togglePlannedPrepItem(item.id).onFailure {
                                                        plannedItems = plannedItems.map { 
                                                            if (it.id == item.id) it.copy(isTicked = if (isDone) 1 else 0) else it 
                                                        }
                                                    }
                                                }
                                            }
                                        )
                                        Column {
                                            Text(
                                                text = item.itemName ?: "Item #${item.prepItemId}",
                                                style = MaterialTheme.typography.titleMedium,
                                                fontWeight = FontWeight.Bold,
                                                color = if (isDone) successOnContainerColor() else MaterialTheme.colorScheme.onSurface
                                            )
                                            Text(
                                                text = "Qty: ${item.quantity}",
                                                style = MaterialTheme.typography.bodyMedium,
                                                color = if (isDone) successContentColor() else MaterialTheme.colorScheme.secondary
                                            )
                                        }
                                    }

                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        if (!item.notes.isNullOrBlank()) {
                                            OutlinedButton(
                                                onClick = { viewingRecipeItem = item },
                                                shape = MaterialTheme.shapes.small,
                                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp)
                                            ) {
                                                Text("View Recipe", style = MaterialTheme.typography.labelSmall)
                                            }
                                        }

                                        if (canEdit) {
                                            IconButton(onClick = { itemToDelete = item }) {
                                                CloseIcon()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                }
            }

            if (showAddModal) {
                AddPlannedPrepItemModal(
                    masterItems = masterItems,
                    onDismiss = { showAddModal = false },
                    onConfirm = { prepItemId, qty ->
                        showAddModal = false
                        scope.launch {
                            val request = SavePlannedPrepItemRequest(
                                date = selectedDate.toString(),
                                prepItemId = prepItemId,
                                quantity = qty
                            )
                            repository.savePlannedPrepItem(request).onSuccess {
                                loadPrep(selectedDate.toString())
                            }
                        }
                    }
                )
            }

            itemToDelete?.let { item ->
                AlertDialog(
                    onDismissRequest = { itemToDelete = null },
                    title = { Text("Delete Prep Item") },
                    text = { Text("Are you sure you want to delete '${item.itemName ?: "Item #${item.prepItemId}"}'?") },
                    confirmButton = {
                        Button(
                            onClick = {
                                val itemToRem = item
                                itemToDelete = null
                                plannedItems = plannedItems.filter { it.id != itemToRem.id }
                                scope.launch {
                                    repository.deletePlannedPrepItem(itemToRem.id)
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                        ) {
                            Text("Delete")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { itemToDelete = null }) {
                            Text("Cancel")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }

            viewingRecipeItem?.let { item ->
                AlertDialog(
                    onDismissRequest = { viewingRecipeItem = null },
                    title = { Text("Recipe: ${item.itemName ?: "Item #${item.prepItemId}"}") },
                    text = {
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(item.notes ?: "No recipe notes available.", style = MaterialTheme.typography.bodyMedium)
                        }
                    },
                    confirmButton = {
                        Button(onClick = { viewingRecipeItem = null }) {
                            Text("Close")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }
        }
    }
}

@Composable
fun AddPlannedPrepItemModal(
    masterItems: List<PrepItem>,
    onDismiss: () -> Unit,
    onConfirm: (String, String) -> Unit
) {
    var selectedItem by remember { mutableStateOf(masterItems.firstOrNull()) }
    var quantity by remember { mutableStateOf("1") }
    var expanded by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Add Prep Item") },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (masterItems.isEmpty()) {
                    Text("No master prep items available. Please create master prep items first.", color = MaterialTheme.colorScheme.error)
                } else {
                    Box(modifier = Modifier.fillMaxWidth()) {
                        OutlinedButton(
                            onClick = { expanded = true },
                            modifier = Modifier.fillMaxWidth(),
                            shape = MaterialTheme.shapes.small
                        ) {
                            Text(selectedItem?.itemName ?: "Select Master Prep Item")
                        }
                        DropdownMenu(
                            expanded = expanded,
                            onDismissRequest = { expanded = false },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            masterItems.forEach { item ->
                                DropdownMenuItem(
                                    text = { Text(item.itemName) },
                                    onClick = {
                                        selectedItem = item
                                        expanded = false
                                    }
                                )
                            }
                        }
                    }
                }

                OutlinedTextField(
                    value = quantity,
                    onValueChange = { quantity = it },
                    label = { Text("Quantity") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    selectedItem?.let { item ->
                        onConfirm(item.id, quantity)
                    }
                },
                enabled = selectedItem != null,
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
